import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AppLockState, CategoryApplicationDto, ModelNameFlags } from '@rocksoft/settings-sdk';
import { Device } from '@rocksoft/shared';
import { DeviceStoreSelectors } from '../device-store';
import { VppApplicationsStoreSelectors } from '../vpp-applications';
import * as fromCategoryConfigurationInherited from './category-configuration-inherited.reducer';
import {
  addIsConfiguredFlagToAppleDevice,
  collectInheritedConfigurations,
  enrichVppApplications,
  groupApplicationsByBundleIdentifier,
  InheritedApplicationConfiguration,
  InheritedVppApplicationConfiguration,
  normalizeAppLockModels,
  selectActiveInheritedApplication,
  selectFirstMatchingInheritedConfiguration,
  withCategoryOrigin,
} from './category-configuration-inherited.selector.helper';
import { CategoryConfigurationInheritedState } from './category-configuration-inherited.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryConfigurationInheritedState = createFeatureSelector<CategoryConfigurationInheritedState>(
  'categoryConfigurationInherited'
);

export const selectAllCategoryConfigurationInherited = createSelector(
  selectCategoryConfigurationInheritedState,
  fromCategoryConfigurationInherited.selectAllCategoryConfigurationInherited
);

export const selectCategoryConfigurationInheritedByCategoryId = (categoryId: string) =>
  createSelector(selectAllCategoryConfigurationInherited, (configurations) =>
    configurations.find((configuration) => configuration.categoryConfiguration.id === categoryId)
  );

const selectNoCategoryConfiguration = createSelector(selectCategoryConfigurationInheritedState, () => undefined);

// ============================================================================
// CONFIGURATION SELECTORS PER SETTING TYPE (INHERITANCE APPLIED)
// ============================================================================

// -------------------------------------------- APPLICATIONS -------------------------------------------- \\

export const selectInheritedApplicationConfigurationByCategoryId = (categoryId: string | null | undefined) =>
  createSelector(
    categoryId ? selectCategoryConfigurationInheritedByCategoryId(categoryId) : selectNoCategoryConfiguration,
    (configuration) =>
      collectInheritedConfigurations(
        configuration,
        (current) => current.applications,
        (application) => `${application.bundleIdentifier ?? ''}:${application.version ?? ''}`
      )
  );

export const selectActiveInheritedApplicationConfigurationByCategoryId = (categoryId: string | null | undefined) =>
  createSelector(selectInheritedApplicationConfigurationByCategoryId(categoryId), (applications) =>
    Object.values(groupApplicationsByBundleIdentifier(applications))
      .map((versions) => selectActiveInheritedApplication(versions))
      .filter((application): application is InheritedApplicationConfiguration => application !== undefined)
  );

export const selectApplicationsForDeviceWithConfigurationByCategoryId = (
  categoryId: string | null | undefined,
  device: Device
) =>
  createSelector(
    DeviceStoreSelectors.selectDeviceByUdid(device.udid),
    selectActiveInheritedApplicationConfigurationByCategoryId(categoryId),
    (selectedDevice: Device | undefined, applications: InheritedApplicationConfiguration[]) =>
      addIsConfiguredFlagToAppleDevice(selectedDevice, applications)
  );

// -------------------------------------------- VPP APPLICATIONS -------------------------------------------- \\

export const selectInheritedVppApplicationConfigurationByCategoryId = (categoryId: string) =>
  createSelector(selectCategoryConfigurationInheritedByCategoryId(categoryId), (configuration) =>
    collectInheritedConfigurations(
      configuration,
      (current) => current.vppApplications,
      (application) => application.adamIdStr ?? ''
    )
  );

export const selectInheritedEnrichedVppApplicationConfigurationByCategoryId = (categoryId: string) =>
  createSelector(
    selectInheritedVppApplicationConfigurationByCategoryId(categoryId),
    VppApplicationsStoreSelectors.selectAllVppApplications,
    (applications, vppApplications): InheritedVppApplicationConfiguration[] =>
      enrichVppApplications(applications, vppApplications)
  );

export const selectLinkedEnrichedVppApplicationConfigurationByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryConfigurationInheritedByCategoryId(categoryId),
    VppApplicationsStoreSelectors.selectAllVppApplications,
    (configuration, vppApplications): InheritedVppApplicationConfiguration[] => {
      const linkedApplications =
        configuration?.categoryConfiguration.vppApplications?.map((application) =>
          withCategoryOrigin(configuration.categoryConfiguration, application)
        ) ?? [];

      return enrichVppApplications(linkedApplications, vppApplications);
    }
  );

export const selectAppliedAppLockByCategoryId = (
  categoryId: string,
  appLockState: AppLockState,
  modelName: ModelNameFlags
) =>
  createSelector(selectCategoryConfigurationInheritedByCategoryId(categoryId), (configuration) => {
    const appLock = selectFirstMatchingInheritedConfiguration(
      configuration,
      (current) => (appLockState === 'Category' && current.id == categoryId ? current.appLocks : current.childAppLocks),
      (current) => (current.models ?? []).includes(modelName)
    );

    return appLock ? normalizeAppLockModels(appLock) : null;
  });

// ============================================================================
// CONFIGURATION SELECTORS PER SETTING TYPE (LINKED TO SPECIFIC CATEGORY)
// ============================================================================

export const selectLinkedApplicationConfigurationByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryConfigurationInheritedByCategoryId(categoryId),
    (configuration) =>
      configuration?.categoryConfiguration.applications?.map((application: CategoryApplicationDto) =>
        withCategoryOrigin(configuration.categoryConfiguration, application)
      ) ?? []
  );
